/**
 * Change a Resource Plan's owner — the field the editor header labels "Author" and the
 * plans table labels "Author" too (RP-15.1). They are the same column
 * (`ownerUserId` / `ownerName`), so there is exactly one place to change it, and it works
 * on a plan in any status — reassignment is not gated on Draft the way editing the grid is.
 */

import { useEffect, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { usePlanOwnerOptions, useSetPlanOwner } from "@/hooks/use-resource-plans";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

interface PlanOwnerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: ResourcePlanDetailView;
}

export function PlanOwnerDialog({ open, onOpenChange, plan }: PlanOwnerDialogProps) {
  const { data: options, isLoading, isError, error, refetch } = usePlanOwnerOptions(open);
  const setOwner = useSetPlanOwner(plan.id);

  // The server sends an email only when two people share a name, so the usual case
  // stays a plain list of names.
  const pickerOptions = (options ?? []).map((u) => ({
    value: u.id,
    label: u.email === null ? u.name : `${u.name} (${u.email})`,
  }));
  const [selected, setSelected] = useState(plan.ownerUserId ?? "");

  // Re-seed from the plan's current owner every time the dialog opens, so a previous,
  // cancelled edit never leaks into the next open — same reset-on-open pattern as
  // WorkstreamDialog.
  useEffect(() => {
    if (open) setSelected(plan.ownerUserId ?? "");
  }, [open, plan.ownerUserId]);

  function submit() {
    if (selected === "" || selected === plan.ownerUserId) {
      onOpenChange(false);
      return;
    }
    setOwner.mutate(selected, { onSuccess: () => onOpenChange(false) });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Change plan Author</DialogTitle>
          <DialogDescription>
            The Author is this plan's owner. Reassigning "{plan.name}" records the change against
            your name.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="plan-owner-picker">Author</Label>
          {isError ? (
            <ErrorState
              message={error instanceof Error ? error.message : "Could not load people."}
              onRetry={refetch}
            />
          ) : (
            <SearchableSelect
              id="plan-owner-picker"
              options={pickerOptions}
              value={selected}
              onValueChange={setSelected}
              placeholder={plan.ownerName ?? "Select an owner…"}
              searchPlaceholder="Search people…"
              loading={isLoading}
            />
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={setOwner.isPending}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={selected === "" || setOwner.isPending}>
            {setOwner.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
